import type { JSX } from 'solid-js'
import { createMemo, For, Show, splitProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Chip from '~/components/Chip'
import Icon from '~/components/Icon'
import Menu from '~/components/Menu'
import type { NodeTypeDTOType, ValidationIssueType, WorkflowNodeExecutionType, WorkflowNodeType } from '~/types/workflow'
import { categoryColor, executionIcon, formatDuration, nodeExecutionClasses, nodeExecutionLabel, nodeIcon } from './workflowMeta'

export interface WorkflowNodeCardPropsType {
  node: WorkflowNodeType
  def?: NodeTypeDTOType | null
  selected: boolean
  /** El nodo es el origen de una conexión en curso. */
  isConnFrom: boolean
  /** Puerto de salida activo cuando se está conectando. */
  connectionPort: string | null
  execution?: WorkflowNodeExecutionType | null
  validationError?: ValidationIssueType | null
  onSelect: (id: string) => void
  onConnectFrom: (id: string, port: string) => void
  onRemove: (id: string) => void
  onDuplicate: (id: string) => void
  onCopy: (id: string) => void
  onToggleDisable: (id: string) => void
  onPointerDown: (e: PointerEvent, id: string) => void
  onOpenConfig: (id: string) => void
}

const NODE_WIDTH = 256

/**
 * Tarjeta de nodo del canvas. Reconoce el tipo al instante (tile de categoría),
 * comunica configuración, estado de ejecución y errores de validación, y
 * expone los puertos de entrada/salida. Todos los controles son componentes de
 * la librería (`Button`, `Menu`, `Chip`).
 */
export function WorkflowNodeCard(props: WorkflowNodeCardPropsType) {
  const [local, rest] = splitProps(props, [
    'node',
    'def',
    'selected',
    'isConnFrom',
    'connectionPort',
    'execution',
    'validationError',
    'onSelect',
    'onConnectFrom',
    'onRemove',
    'onDuplicate',
    'onCopy',
    'onToggleDisable',
    'onPointerDown',
    'onOpenConfig'
  ])

  const outputs = () => local.def?.ports.outputs ?? []
  const hasInput = () => (local.def?.ports.inputs.length ?? 0) > 0

  /** La tarjeta queda configurada si tiene al menos un campo obligatorio cubierto. */
  const isConfigured = createMemo(() => {
    const required = local.def?.fields.filter(f => f.required) ?? []
    if (!required.length) return Object.keys(local.node.config ?? {}).length > 0
    return required.every(f => {
      const v = local.node.config?.[f.key]
      return v !== undefined && v !== null && v !== ''
    })
  })

  const portPositions = createMemo(() => {
    const count = outputs().length
    if (count === 0) return []
    if (count === 1) return ['top-9']
    return outputs().map((_, i) => (count === 2 ? (i === 0 ? 'top-6' : 'bottom-6') : `top-${6 + i * 8}`))
  })

  const executionState = createMemo(() => {
    const ex = local.execution
    if (!ex) return null
    return {
      label: nodeExecutionLabel(ex.status),
      icon: executionIcon(ex.status),
      cls: nodeExecutionClasses(ex.status),
      duration: formatDuration(ex.durationMs)
    }
  })

  const borderClass = () => {
    if (local.selected) return 'border-primary ring-2 ring-primary/25 shadow-md'
    if (local.isConnFrom) return 'border-info ring-2 ring-info/20 shadow-md'
    if (local.execution) return local.execution.status === 'failed' ? 'border-danger' : local.execution.status === 'succeeded' ? 'border-success/60' : 'border-info/50'
    if (local.validationError) return 'border-warning'
    return 'border-border hover:border-primary/50'
  }

  const style: JSX.CSSProperties = {
    left: `${local.node.position.x}px`,
    top: `${local.node.position.y}px`,
    width: `${NODE_WIDTH}px`
  }

  return (
    <div
      {...rest}
      data-testid="workflow-node"
      data-node-id={local.node.id}
      class={twMerge('absolute select-none rounded-xl border-2 bg-surface p-3 shadow-sm transition-shadow', borderClass(), local.node.disabled ? 'opacity-75' : '')}
      style={style}
      onClick={e => {
        e.stopPropagation()
        local.onSelect(local.node.id)
      }}
      onPointerDown={e => local.onPointerDown(e, local.node.id)}
    >
      {/* Handles de entrada */}
      <Show when={hasInput()}>
        <span data-testid="workflow-node-input" class="absolute -left-3 top-9 z-10 h-3.5 w-3.5 rounded-full border-2 border-surface bg-foreground-subtle shadow-sm" title="Entrada" />
      </Show>

      {/* Handles de salida */}
      <For each={outputs()}>
        {(port, i) => {
          const isActive = local.isConnFrom && local.connectionPort === port.key
          return (
            <Button
              size="xs"
              variant={isActive ? 'contained' : 'soft'}
              color="info"
              icon="material-symbols-light:link"
              aria-label={`Conectar por ${port.label}`}
              tooltip={isActive ? `Cancelar conexión por ${port.label}` : `Conectar por ${port.label}`}
              class={twMerge('absolute z-20 -right-3 h-7 w-7 !rounded-full !p-0', portPositions()[i()])}
              onClick={e => {
                e.stopPropagation()
                local.onConnectFrom(local.node.id, port.key)
              }}
            />
          )
        }}
      </For>

      {/* Cabecera: tile de categoría + nombre + estado de ejecución */}
      <div class="flex items-center gap-2.5">
        <span class={twMerge('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', categoryColor(local.def?.category ?? 'data'))}>
          <Icon icon={nodeIcon(local.def?.icon)} size={20} />
        </span>
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-semibold text-foreground">{local.node.name}</p>
          <p class="truncate text-[11px] text-foreground-subtle">{local.def?.category ?? local.node.type}</p>
        </div>
        <Show when={executionState()}>
          <span class={twMerge('flex h-6 w-6 shrink-0 items-center justify-center rounded-full', executionState()!.cls.split(' ')[1])}>
            <Icon icon={executionState()!.icon} size={14} />
          </span>
        </Show>
      </div>

      {/* Descripción */}
      <p class="mt-2 line-clamp-2 text-xs text-foreground-subtle">{local.def?.description ?? local.node.type}</p>

      {/* Estado de ejecución o configuración */}
      <Show
        when={executionState()}
        fallback={
          <div class="mt-2.5">
            <Chip
              size="xs"
              variant="soft"
              color={isConfigured() ? 'success' : 'warning'}
              icon={isConfigured() ? 'material-symbols-light:check' : 'material-symbols-light:warning-outline'}
              iconSize={12}
            >
              {isConfigured() ? 'Configurado' : 'Requiere configuración'}
            </Chip>
          </div>
        }
      >
        <div class="mt-2.5 flex items-center gap-1.5 text-[11px] font-medium text-foreground-muted">
          <span class={twMerge('h-1.5 w-1.5 rounded-full', executionState()!.cls.split(' ')[2] ?? 'bg-current')} />
          <span>{executionState()!.label}</span>
          <Show when={executionState()!.duration}>
            <span class="font-mono text-[10px] text-foreground-subtle">{executionState()!.duration}</span>
          </Show>
        </div>
      </Show>

      {/* Error de validación */}
      <Show when={local.validationError}>
        <Chip size="xs" variant="soft" color="warning" icon="material-symbols-light:warning" iconSize={12} class="mt-2 max-w-full !whitespace-normal">
          {local.validationError!.message}
        </Chip>
      </Show>

      {/* Barra de acciones del nodo */}
      <div class="mt-2.5 flex items-center gap-1 border-t border-border/70 pt-2">
        <Show when={outputs().length}>
          <span class="mr-1 text-[10px] uppercase tracking-wide text-foreground-subtle">Salidas</span>
        </Show>
        <div class="flex-1" />
        <ButtonGroup size="xs" variant="soft" color="default">
          <Show when={!local.node.disabled}>
            <Button
              icon="material-symbols-light:content-copy"
              iconSize={15}
              aria-label="Duplicar nodo"
              tooltip="Duplicar"
              onClick={e => {
                e.stopPropagation()
                local.onDuplicate(local.node.id)
              }}
            />
            <Button
              icon="material-symbols-light:copy-outline"
              iconSize={15}
              aria-label="Copiar nodo"
              tooltip="Copiar (Ctrl+C)"
              onClick={e => {
                e.stopPropagation()
                local.onCopy(local.node.id)
              }}
            />
          </Show>
          <Button
            icon="material-symbols-light:close"
            iconSize={15}
            aria-label="Eliminar nodo"
            tooltip="Eliminar"
            onClick={e => {
              e.stopPropagation()
              local.onRemove(local.node.id)
            }}
          />
          <Menu
            trigger={<Button icon="material-symbols-light:more-vert" iconSize={15} aria-label="Más acciones del nodo" tooltip="Acciones" />}
            items={[
              {
                label: 'Editar',
                icon: 'material-symbols-light:edit-outline',
                onClick: () => local.onOpenConfig(local.node.id)
              },
              { label: 'Duplicar', icon: 'material-symbols-light:content-copy', onClick: () => local.onDuplicate(local.node.id) },
              { label: 'Copiar', icon: 'material-symbols-light:copy-outline', onClick: () => local.onCopy(local.node.id) },
              {
                label: local.node.disabled ? 'Habilitar' : 'Deshabilitar',
                icon: local.node.disabled ? 'material-symbols-light:visibility-outline' : 'material-symbols-light:visibility-off-outline',
                onClick: () => local.onToggleDisable(local.node.id)
              },
              { label: 'Eliminar', icon: 'material-symbols-light:delete-outline', color: 'danger', onClick: () => local.onRemove(local.node.id) }
            ]}
          />
        </ButtonGroup>
      </div>
    </div>
  )
}

export { NODE_WIDTH }
